import { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PageContainer from 'components/PageContainer';
import { numberToCurrencyString } from 'further-ui/utils';
import { useMutateShareSplit } from 'hooks/data/company/useShareSplits';
import { createCompanyEditRoute } from 'adminConstants/routes';
import DetailsStep from './DetailsStep';
import SummaryStep from './SummaryStep';
import useCompany from 'hooks/data/company/useCompany';
import { format } from 'date-fns';
import PageContent from 'components/PageContent';
import { round } from 'lodash';

const steps = {
  DETAILS: 0,
  SUMMARY: 1,
};

const AddShareSplit: React.FC = () => {
  const history = useHistory();
  const { companyId } = useParams<{ companyId: string }>();
  const [step, setStep] = useState(steps.DETAILS);
  const [shareSplitFactor, setShareSplitFactor] = useState(null);
  const [shareSplitDate, setShareSplitDate] = useState<Date>();
  const companyQuery = useCompany(companyId);
  const { createShareSplit } = useMutateShareSplit();

  useEffect(() => {
    setStep(steps.DETAILS);
  }, [companyId]);

  const currentSharePrice = companyQuery.data?.currentSharePrice || 0;
  const currentSharePriceDisplay = numberToCurrencyString(currentSharePrice, {
    unlimitedDp: true,
  });
  // This prevents an issue with JS where it cannot accurately represent some
  // decimal numbers. For example, 2.9 / 100 = 0.028999999999999998. In this
  // case we can assume that a share price will never need to be more than 13
  // decimal places, so anything above that is a maths error that can be rounded out.
  const newSharePrice = round(
    (currentSharePrice ?? 0) / Number(shareSplitFactor),
    13,
  );
  const newSharePriceDisplay = numberToCurrencyString(newSharePrice, {
    unlimitedDp: true,
  });

  const onSubmit = async () => {
    const data = {
      companyId,
      date: format(shareSplitDate!, 'dd/MM/yyyy'),
      shareSplitFactor: Number(shareSplitFactor),
      sharePricePreSplit: currentSharePrice,
      sharePricePostSplit: newSharePrice,
    };

    if (createShareSplit.isPending) return;
    createShareSplit.mutate(data, {
      onSuccess: () => {
        history.push(createCompanyEditRoute(companyId));
      },
    });
  };

  return (
    <PageContainer heading="Update company: Split shares">
      <PageContent header="Split shares">
        {step === steps.DETAILS && (
          <DetailsStep
            company={companyQuery}
            shareSplitDate={shareSplitDate}
            setShareSplitDate={setShareSplitDate}
            shareSplitFactor={shareSplitFactor}
            setShareSplitFactor={setShareSplitFactor}
            steps={steps}
            setStep={setStep}
            currentSharePriceDisplay={currentSharePriceDisplay}
          />
        )}

        {step === steps.SUMMARY && (
          <SummaryStep
            company={companyQuery}
            shareSplitDate={shareSplitDate}
            shareSplitFactor={shareSplitFactor}
            currentSharePriceDisplay={currentSharePriceDisplay}
            newSharePriceDisplay={newSharePriceDisplay}
            onSubmit={onSubmit}
            onBack={() => setStep(steps.DETAILS)}
            isSubmitting={createShareSplit.isPending}
          />
        )}
      </PageContent>
    </PageContainer>
  );
};

export default AddShareSplit;
